import React, { useMemo, useCallback } from 'react';
import { Users } from 'lucide-react';
import BiographySection from './BiographySection';
import EditableContent from './EditableContent';
import { BiographyField } from './types';
import { CharacterData } from '../../types/character';

interface ConnectionsSectionProps {
  character: CharacterData;
  updateCharacterData: (updates: any) => void;
  defaultExpanded?: boolean;
  priority?: 'high' | 'medium' | 'low';
}

/**
 * ConnectionsSection - Specialized section for party connections
 * Uses BiographySection + EditableContent composition
 */
const ConnectionsSection: React.FC<ConnectionsSectionProps> = ({
  character,
  updateCharacterData,
  defaultExpanded = true,
  priority = 'medium'
}) => {
  // Convert character connections data to biography fields
  const fields = useMemo((): BiographyField[] => {
    const connectionAnswers = character.biography?.connectionAnswers || [];

    return connectionAnswers.map((connection, index) => ({
      id: `connection-${index}`,
      label: connection.prompt || `Connection ${index + 1}`,
      value: connection.answer || '',
      type: 'textarea' as const,
      editable: true,
      placeholder: 'Describe your connection with another party member...',
      maxLength: 400
    }));
  }, [character.biography?.connectionAnswers]);

  // Handle field changes
  const handleFieldChange = useCallback((fieldId: string, value: string | string[]) => {
    const connectionAnswers = [...(character.biography?.connectionAnswers || [])];
    const connectionIndex = parseInt(fieldId.replace('connection-', ''));

    if (connectionIndex >= 0 && connectionIndex < connectionAnswers.length) {
      connectionAnswers[connectionIndex] = {
        ...connectionAnswers[connectionIndex],
        answer: typeof value === 'string' ? value : value[0] || ''
      };

      const updates = {
        biography: {
          ...character.biography,
          connectionAnswers
        }
      };
      updateCharacterData(updates);
    }
  }, [character.biography, updateCharacterData]);

  // Handle save (for any additional validation or processing)
  const handleSave = useCallback(async () => {
    // Additional save logic if needed
    console.log('Connections data saved');
  }, []);

  // Never mark as empty - always show fields so users can edit/add
  const isEmpty = false;

  return (
    <BiographySection
      sectionId="bio-connections"
      title="Party Connections"
      icon={<Users size={18} />}
      defaultExpanded={defaultExpanded}
      priority={priority}
      isEmpty={isEmpty}
      emptyMessage="No party connections established yet"
    >
      {fields.length === 0 ? (
        <div className="biography-empty">
          <p className="biography-empty__message">
            No party connection prompts from character creation. You can add custom connections in other sections.
          </p>
        </div>
      ) : (
        <EditableContent
          fields={fields}
          onFieldChange={handleFieldChange}
          onSave={handleSave}
          maxFieldsPerRow={1}
          showLabels={true}
          useCard={false}
        />
      )}
    </BiographySection>
  );
};

export default ConnectionsSection;